import { createContext, useContext, useEffect, useRef } from "react";

/**
 * Flush registry
 *
 * Per-platform field editors commit their edits via a `useDebouncedCallback`
 * (from `use-debounce`). Switching platform, toggling "Customise", or toggling
 * "Story" remounts / unmounts these editors, which CANCELS the pending debounce
 * and drops the last in-flight edit.
 *
 * The fix: every debounced editor registers its `.flush()` into this central
 * registry, and the composer calls `flushPending()` SYNCHRONOUSLY before each
 * remount-causing dispatch — so the flushed edit commits while the OLD
 * selectedPlatform is still active and routes to the correct platform.
 *
 * `use-debounce` `.flush()` fires the trailing call synchronously if one is
 * pending and is a safe no-op otherwise.
 */

export interface FlushRegistryValue {
  register: (fn: () => void) => () => void;
  flushPending: () => void;
}

export const FlushRegistryContext = createContext<FlushRegistryValue | null>(
  null
);

/**
 * Registers a debounced editor's `.flush()` into the registry for the lifetime
 * of the component. A STABLE wrapper is registered once on mount; it always
 * calls the LATEST `flush` (kept in a ref) so a recreated debounce instance is
 * still flushed correctly. Tolerant: a no-op when there is no provider.
 */
export function useRegisterFlush(flush: () => void): void {
  const ctx = useContext(FlushRegistryContext);
  const flushRef = useRef(flush);

  // Keep the ref pointing at the latest debounced instance (updated after each
  // render, not during it) so the stable registered wrapper always flushes the
  // current debounce.
  useEffect(() => {
    flushRef.current = flush;
  });

  useEffect(() => {
    if (!ctx) {
      return;
    }
    const stableFlush = () => {
      flushRef.current?.();
    };
    return ctx.register(stableFlush);
    // Only (un)register on mount/unmount (and if the provider identity changes).
  }, [ctx]);
}

/**
 * Returns a function that synchronously flushes all pending debounced editors.
 * Tolerant: a no-op when called outside a provider.
 */
export function useFlushPending(): () => void {
  const ctx = useContext(FlushRegistryContext);
  return ctx?.flushPending ?? (() => {});
}
